import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";

import { galleryQuery } from "@/lib/queries";
import { EmptyState } from "@/components/site/bits";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export const Route = createFileRoute("/galeria/$slug")({
  head: ({ loaderData }) => {
    const d = loaderData as any;
    if (!d) {
      return {
        meta: [
          { title: "Álbum não encontrado | Professor Rurdiney — 40123" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const title = `${d.gallery.title} | Galeria | Professor Rurdiney — 40123`;
    const description = d.gallery.description || "Álbum de fotos da campanha do Professor Rurdiney.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
      ],
    };
  },
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(galleryQuery(params.slug));
    if (!data) throw notFound();
    return data;
  },
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center gap-4 px-4 py-32 text-center">
      <h1 className="text-3xl text-balance-title">Álbum não encontrado</h1>
      <p className="text-muted-foreground">Esse álbum não existe ou foi removido.</p>
      <Link
        to="/galeria"
        className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground"
      >
        <ArrowLeft className="size-4" /> Voltar à galeria
      </Link>
    </div>
  ),
  component: GaleriaDetail,
});

function GaleriaDetail() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(galleryQuery(slug));
  const [active, setActive] = useState<string | null>(null);
  if (!data) return null;
  const { gallery, images } = data;

  return (
    <>
      <header className="relative overflow-hidden bg-hero-gradient px-4 pt-28 pb-16 text-primary-foreground md:pt-36 md:pb-20">
        <div className="absolute inset-0 bg-mesh opacity-40" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-6xl">
          <Link
            to="/galeria"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-foreground/80 hover:text-primary-foreground"
          >
            <ArrowLeft className="size-4" /> Todos os álbuns
          </Link>
          <h1 className="mt-6 text-3xl md:text-5xl text-balance-title">{gallery.title}</h1>
          {gallery.description && (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-primary-foreground/85">
              {gallery.description}
            </p>
          )}
        </div>
      </header>

      <section className="px-4 py-16 md:py-20">
        <div className="mx-auto w-full max-w-6xl">
          {images.length === 0 ? (
            <EmptyState message="Este álbum ainda não possui fotos." />
          ) : (
            <div className="columns-2 gap-4 md:columns-3 [&>*]:mb-4">
              {images.map((img: any) => (
                <button
                  key={img.id}
                  onClick={() => setActive(img.image_url)}
                  className="block w-full overflow-hidden rounded-2xl border border-border shadow-card"
                >
                  <img
                    src={img.image_url}
                    alt={img.caption || gallery.title}
                    className="w-full object-cover transition-transform hover:scale-105"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-4xl border-none bg-transparent p-0 shadow-none">
          {active && (
            <img src={active} alt={gallery.title} className="max-h-[85vh] w-full rounded-2xl object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
